import type { SoundPlayDTO } from '@clanfire/shared';
import { useVoice } from '@/stores/realtime';
import { useVoiceSettings } from '@/stores/voiceSettings';

/**
 * Soundboard playback. The built-in sounds are made here with WebAudio (no
 * audio files to download or license); a server's own sounds are short clips.
 * Sounds play for everyone in the voice channel, at each person's soundboard
 * volume, and not at all while they're deafened.
 */

let ctx: AudioContext | null = null;
let noise: AudioBuffer | null = null;

function audio(): AudioContext {
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** One second of white noise, for drums, claps and cymbals. */
function noiseBuffer(c: AudioContext): AudioBuffer {
  if (noise && noise.sampleRate === c.sampleRate) return noise;
  noise = c.createBuffer(1, c.sampleRate, c.sampleRate);
  const data = noise.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return noise;
}

type Out = { c: AudioContext; out: AudioNode; t: number };

/** A note with a quick attack and a release. */
function tone(o: Out, type: OscillatorType, freq: number, start: number, length: number, level = 0.3, endFreq?: number) {
  const { c, out, t } = o;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t + start);
  if (endFreq) osc.frequency.exponentialRampToValueAtTime(endFreq, t + start + length);
  gain.gain.setValueAtTime(0.0001, t + start);
  gain.gain.exponentialRampToValueAtTime(level, t + start + 0.015);
  gain.gain.setValueAtTime(level, t + start + Math.max(0.02, length - 0.06));
  gain.gain.exponentialRampToValueAtTime(0.0001, t + start + length);
  osc.connect(gain).connect(out);
  osc.start(t + start);
  osc.stop(t + start + length + 0.05);
  return osc;
}

/** A burst of filtered noise (a clap, a snare, a cymbal). */
function burst(o: Out, start: number, length: number, level: number, filter: BiquadFilterType, freq: number) {
  const { c, out, t } = o;
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c);
  const f = c.createBiquadFilter();
  f.type = filter;
  f.frequency.value = freq;
  const gain = c.createGain();
  gain.gain.setValueAtTime(level, t + start);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + start + length);
  src.connect(f).connect(gain).connect(out);
  src.start(t + start, Math.random() * 0.5);
  src.stop(t + start + length + 0.05);
}

/** A drum hit: a sine that drops in pitch. */
function drum(o: Out, start: number, from: number, to: number, level = 0.8) {
  tone(o, 'sine', from, start, 0.25, level, to);
}

const BUILTINS: Record<string, (o: Out) => void> = {
  'builtin:airhorn': (o) => {
    const lp = o.c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2400;
    lp.connect(o.out);
    const horn = { ...o, out: lp };
    for (const [start, length] of [
      [0, 0.22],
      [0.28, 0.22],
      [0.56, 0.9],
    ] as const) {
      for (const freq of [370, 466, 554]) tone(horn, 'sawtooth', freq, start, length, 0.12, freq * 0.98);
    }
  },
  'builtin:rimshot': (o) => {
    drum(o, 0, 220, 110);
    drum(o, 0.2, 170, 85);
    burst(o, 0.42, 0.9, 0.35, 'highpass', 6000);
  },
  'builtin:tada': (o) => {
    tone(o, 'triangle', 523, 0, 0.14, 0.3);
    tone(o, 'triangle', 659, 0.12, 0.14, 0.3);
    for (const freq of [523, 659, 784, 1047]) tone(o, 'triangle', freq, 0.3, 1.1, 0.18);
    tone(o, 'sawtooth', 262, 0.3, 1.1, 0.05);
  },
  'builtin:sad': (o) => {
    const lp = o.c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1100;
    lp.connect(o.out);
    const brass = { ...o, out: lp };
    tone(brass, 'sawtooth', 293, 0, 0.4, 0.22, 285);
    tone(brass, 'sawtooth', 277, 0.45, 0.4, 0.22, 270);
    tone(brass, 'sawtooth', 261, 0.9, 0.45, 0.22, 255);
    // The long, wobbly last note.
    const last = tone(brass, 'sawtooth', 246, 1.4, 1.3, 0.22, 230);
    const lfo = o.c.createOscillator();
    const depth = o.c.createGain();
    lfo.frequency.value = 6;
    depth.gain.value = 6;
    lfo.connect(depth).connect(last.frequency);
    lfo.start(o.t + 1.5);
    lfo.stop(o.t + 2.8);
  },
  'builtin:boing': (o) => {
    const osc = tone(o, 'triangle', 140, 0, 0.7, 0.35);
    osc.frequency.exponentialRampToValueAtTime(700, o.t + 0.18);
    osc.frequency.exponentialRampToValueAtTime(260, o.t + 0.7);
    const lfo = o.c.createOscillator();
    const depth = o.c.createGain();
    lfo.frequency.value = 22;
    depth.gain.setValueAtTime(90, o.t);
    depth.gain.exponentialRampToValueAtTime(1, o.t + 0.7);
    lfo.connect(depth).connect(osc.frequency);
    lfo.start(o.t);
    lfo.stop(o.t + 0.75);
  },
  'builtin:crickets': (o) => {
    for (let group = 0; group < 5; group++) {
      for (let chirp = 0; chirp < 3; chirp++) tone(o, 'sine', 4400 + (group % 2) * 300, group * 0.42 + chirp * 0.055, 0.035, 0.08);
    }
  },
  'builtin:applause': (o) => {
    for (let i = 0; i < 90; i++) {
      const at = Math.random() * 2.2;
      // Louder in the middle, like a crowd.
      const level = 0.12 * Math.sin((Math.PI * at) / 2.2) + 0.03;
      burst(o, at, 0.05 + Math.random() * 0.04, level, 'bandpass', 900 + Math.random() * 1600);
    }
  },
  'builtin:drumroll': (o) => {
    for (let i = 0; i < 34; i++) burst(o, i * 0.045, 0.06, 0.06 + (i / 34) * 0.2, 'bandpass', 1800);
    drum(o, 1.55, 160, 70, 0.9);
    burst(o, 1.55, 1.2, 0.35, 'highpass', 5000);
  },
};

const playing = new Set<HTMLAudioElement>();

/** Plays a sound at a volume from 0 to 1. */
export function playSound(sound: Pick<SoundPlayDTO, 'id' | 'url'>, volume: number) {
  if (volume <= 0) return;
  if (sound.url) {
    // A server's own clip. At most three at once, so a burst can't pile up.
    if (playing.size >= 3) return;
    const el = new Audio(sound.url);
    el.volume = Math.min(1, volume);
    playing.add(el);
    el.addEventListener('ended', () => playing.delete(el));
    el.addEventListener('error', () => playing.delete(el));
    void el.play().catch(() => playing.delete(el));
    return;
  }
  const make = BUILTINS[sound.id];
  if (!make) return;
  const c = audio();
  const master = c.createGain();
  master.gain.value = Math.min(1, volume);
  master.connect(c.destination);
  make({ c, out: master, t: c.currentTime + 0.02 });
}

/** A sound someone in your voice channel played: at your soundboard volume, unless you're deafened. */
export function playSoundboard(sound: SoundPlayDTO) {
  if (useVoice.getState().deafened) return;
  playSound(sound, useVoiceSettings.getState().soundboardVolume / 100);
}
